import React, { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { 
  ChevronLeft, 
  Shuffle, 
  Pause, 
  Play, 
  RotateCcw,
  Settings,
  Trophy,
  Calendar,
  Swords,
  History,
  Menu,
  HelpCircle,
  Bell,
  X,
  Maximize,
  Minimize,
  Lightbulb,
  Download
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { GameStats } from './GameStats';
import { DifficultyDropdown } from './DifficultyDropdown';
import { GameActionButtons } from './GameActionButtons';
import { NotificationBell } from '../NotificationBell';
import { useFullscreen } from '../../../hooks/useFullscreen';
import { usePWAInstall } from '../../../hooks/usePWAInstall';

export function MobileHeader({
  time,
  moves,
  difficulty,
  onDifficultySelect,
  isPaused,
  isRunning,
  onPause,
  onPrevious,
  onNewImage,
  canGoBack,
  hasNewImages,
  onReset,
  onSettings,
  onLeaderboard,
  onDailyChallenge,
  onChallengeFriend,
  onHistory,
  onHelp,
  onHint,
  hasPlayedDailyToday,
  notificationCount,
  notifications,
  onMarkNotificationsRead,
  onViewChallengeNotification,
  isLandscape,
  hasUnplacedPieces,
  isComplete
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isFullscreen, isSupported: fullscreenSupported, toggleFullscreen } = useFullscreen();
  const { promptInstall } = usePWAInstall();
  
  // Menu item handler that closes menu after action
  const handleMenuAction = useCallback((action) => {
    setIsMenuOpen(false);
    action?.();
  }, []);

  // Shared dropdown menu content — used in both portrait and landscape
  const menuContent = (
    <DropdownMenuContent align="end" className="w-56">
      <DropdownMenuItem 
        onClick={() => handleMenuAction(onPrevious)}
        disabled={!canGoBack}
        className={cn(!canGoBack && "opacity-50")}
      >
        <ChevronLeft className="h-4 w-4 mr-2" />
        {canGoBack ? 'Previous Image' : 'No Previous Images'}
      </DropdownMenuItem>
      <DropdownMenuItem 
        onClick={() => handleMenuAction(onNewImage)} 
        className={cn(!hasNewImages && "opacity-50")}
      >
        <Shuffle className="h-4 w-4 mr-2" />
        {hasNewImages ? 'New Image' : 'No New Images'}
      </DropdownMenuItem>
      
      <DropdownMenuSeparator />
      
      <DropdownMenuItem onClick={() => handleMenuAction(onPause)} disabled={!isRunning}>
        {isPaused ? (
          <>
            <Play className="h-4 w-4 mr-2" />
            Resume
          </>
        ) : (
          <>
            <Pause className="h-4 w-4 mr-2" />
            Pause
          </>
        )}
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => handleMenuAction(onReset)}>
        <RotateCcw className="h-4 w-4 mr-2" />
        Reset Puzzle
      </DropdownMenuItem>
      
      <DropdownMenuSeparator />
      
      <DropdownMenuItem onClick={() => handleMenuAction(onLeaderboard)}>
        <Trophy className="h-4 w-4 mr-2" />
        Leaderboard
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => handleMenuAction(onDailyChallenge)}>
        <Calendar className="h-4 w-4 mr-2" />
        Daily Challenge
        {!hasPlayedDailyToday && (
          <span className="ml-auto w-2 h-2 bg-destructive rounded-full" />
        )}
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => handleMenuAction(onChallengeFriend)}>
        <Swords className="h-4 w-4 mr-2" />
        My Challenges
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => handleMenuAction(onHistory)}>
        <History className="h-4 w-4 mr-2" />
        Puzzle History
      </DropdownMenuItem>
      
      {notificationCount > 0 && (
        <DropdownMenuItem className="text-primary">
          <Bell className="h-4 w-4 mr-2" />
          Notifications
          <span className="ml-auto bg-destructive text-destructive-foreground text-xs rounded-full px-1.5">
            {notificationCount}
          </span>
        </DropdownMenuItem>
      )}
      
      <DropdownMenuSeparator />
      
      {/* Fullscreen option */}
      {fullscreenSupported && (
        <DropdownMenuItem onClick={() => {
          toggleFullscreen();
          setIsMenuOpen(false);
        }}>
          {isFullscreen ? (
            <>
              <Minimize className="h-4 w-4 mr-2" />
              Exit Fullscreen
            </>
          ) : (
            <>
              <Maximize className="h-4 w-4 mr-2" />
              Fullscreen Mode
            </>
          )}
        </DropdownMenuItem>
      )}
      
      <DropdownMenuItem onClick={() => handleMenuAction(onSettings)}>
        <Settings className="h-4 w-4 mr-2" />
        Settings
      </DropdownMenuItem>
      
      <DropdownMenuItem onClick={() => handleMenuAction(onHelp)}>
        <HelpCircle className="h-4 w-4 mr-2" />
        Help
      </DropdownMenuItem>
      
      {/* Install App option - always visible */}
      <DropdownMenuItem onClick={() => {
        setIsMenuOpen(false);
        promptInstall();
      }}>
        <Download className="h-4 w-4 mr-2" />
        Install App
      </DropdownMenuItem>
    </DropdownMenuContent>
  );
  
  return (
    <div className={cn("flex flex-col gap-1", isLandscape && "gap-0")}>
      {/* Row 1: Back, Logo, Time, Difficulty, Moves */}
      <div className={cn(
        "flex items-center justify-between gap-1 sm:gap-2",
        isLandscape && "gap-2"
      )}>
        {/* Close / Exit App Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={async () => {
            // 1. If the page entered the Fullscreen API (auto-enabled on first
            //    interaction), exit it first. Don't early-return — the user
            //    expects tapping X to actually leave the app, not just defullscreen.
            const inFullscreenApi =
              !!document.fullscreenElement || !!document.webkitFullscreenElement;
            if (inFullscreenApi && fullscreenSupported) {
              try { await toggleFullscreen(); } catch { /* ignore */ }
            }

            // 2. Detect installed PWA / standalone mode.
            const isStandalone =
              window.matchMedia?.('(display-mode: standalone)').matches ||
              window.matchMedia?.('(display-mode: fullscreen)').matches ||
              window.matchMedia?.('(display-mode: minimal-ui)').matches ||
              window.navigator.standalone === true ||
              document.referrer.startsWith('android-app://');

            // 3. For regular browser tabs with navigation history, prefer history.back()
            //    so the user returns to wherever they came from.
            if (!isStandalone && window.history.length > 1) {
              try {
                window.history.back();
                return;
              } catch { /* ignore */ }
            }

            // 4. Try window.close(). On Android Chrome PWA / TWA this works.
            //    iOS never lets scripts close windows; desktop only allows it for
            //    script-opened popups.
            try { window.close(); } catch { /* ignore */ }

            // 5. If we're still here after a short delay, close() was blocked.
            //    Show a platform-appropriate message so users know what to do.
            setTimeout(() => {
              if (!document.hidden) {
                toast.info(
                  isStandalone
                    ? 'Swipe the app away from Recent Apps to fully exit.'
                    : 'Please close this tab to exit the app.'
                );
              }
            }, 400);
          }}
          className={cn(
            "h-8 w-8 flex-shrink-0",
            isLandscape && "h-6 w-6"
          )}
          data-testid="close-app-btn"
          aria-label="Close app"
        >
          <X className={cn("h-4 w-4", isLandscape && "h-3 w-3")} />
        </Button>
        
        {/* Logo */}
        <div 
          className={cn(
            "rounded-lg select-none flex-shrink-0 overflow-hidden",
            isLandscape ? "w-6 h-6" : "w-8 h-8"
          )}
        >
          <img 
            src="/app-logo.png" 
            alt="Jigsaw Blocks" 
            className="w-full h-full object-contain"
          />
        </div>
      
        <GameStats time={time} moves={moves} isLandscape={isLandscape} variant="mobile" />
        
        <DifficultyDropdown 
          difficulty={difficulty} 
          onDifficultySelect={onDifficultySelect} 
          isLandscape={isLandscape}
          variant="mobile"
        />
      
        {/* In landscape, show essential controls inline with stats */}
        {isLandscape && (
          <div className="flex items-center gap-1 ml-2">
            <GameActionButtons
              onPrevious={onPrevious}
              onNewImage={onNewImage}
              onPause={onPause}
              onReset={onReset}
              onHint={onHint}
              canGoBack={canGoBack}
              hasNewImages={hasNewImages}
              isPaused={isPaused}
              isRunning={isRunning}
              hasUnplacedPieces={hasUnplacedPieces}
              isComplete={isComplete}
              isLandscape={isLandscape}
              variant="landscape"
            />
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={onHint}
                    disabled={!hasUnplacedPieces || isComplete || isPaused}
                    className={cn(
                      "h-7 w-7",
                      (!hasUnplacedPieces || isComplete || isPaused) && "opacity-50"
                    )}
                    data-testid="landscape-hint-btn"
                  >
                    <Lightbulb className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Hint (-25 pts)</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {/* Menu button (landscape) - opens the same menu as portrait */}
            <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  data-testid="landscape-menu-btn"
                  aria-label="Menu"
                >
                  <Menu className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              {menuContent}
            </DropdownMenu>
          </div>
        )}
      </div>
      
      {/* Row 2: Menu and Help buttons on the right (portrait mobile only) */}
      {!isLandscape && (
        <div className="flex items-center justify-end gap-1 sm:gap-2 flex-wrap">
          {/* Mobile Menu Button */}
          <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                size="sm" 
                className="h-8 px-1.5 sm:px-2 gap-1"
                data-testid="mobile-menu-btn"
              >
                <Menu className="h-4 w-4" />
                <span className="text-xs hidden xs:inline">Menu</span>
              </Button>
            </DropdownMenuTrigger>
            {menuContent}
          </DropdownMenu>

          {/* Hint Button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className={cn(
                    "h-8 px-1.5 sm:px-2 gap-1",
                    (!hasUnplacedPieces || isComplete || isPaused) && "opacity-50"
                  )}
                  onClick={onHint}
                  disabled={!hasUnplacedPieces || isComplete || isPaused}
                  data-testid="mobile-hint-btn"
                >
                  <Lightbulb className="h-4 w-4" />
                  <span className="text-xs hidden xs:inline">Hint</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Hint (-25 pts)</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Notification Bell */}
          <NotificationBell
            notifications={notifications}
            count={notificationCount}
            onMarkAsRead={onMarkNotificationsRead}
            onViewChallenge={onViewChallengeNotification}
          />
          
          {/* Help Button */}
          <Button 
            variant="outline" 
            size="sm" 
            className="h-8 px-1.5 sm:px-2 gap-1"
            onClick={onHelp}
            data-testid="mobile-help-btn"
          >
            <HelpCircle className="h-4 w-4" />
            <span className="text-xs hidden xs:inline">Help</span>
          </Button>
        </div>
      )}
    </div>
  );
}
